// src/ui/EmailVerificationNotice.tsx
//
// Hláška „ověřte e-mail“ s cestou k nápravě (bezpečnost 2): přihlášku na
// závod pustí pravidla jen s ověřeným e-mailem. Ukazuje ji přihláška
// (PaymentRace – místo formuláře) a přehled střelce (UserDashboard).
// Stav a akce dodává useEmailVerification (utils/emailVerification).

import React from "react";
import { InfoCard } from "./Cards";
import { useToast } from "./Toast";
import { firebaseErrorMessage } from "../utils/firebaseErrors";
import { reportWarning } from "../utils/reportError";
import type { EmailVerificationState } from "../utils/emailVerification";

interface EmailVerificationNoticeProps {
  state: EmailVerificationState;
  /** "race" = místo formuláře přihlášky, "dashboard" = upozornění na přehledu. */
  variant: "race" | "dashboard";
  className?: string;
}

export const EmailVerificationNotice: React.FC<EmailVerificationNoticeProps> = ({
  state,
  variant,
  className,
}) => {
  const { showToast } = useToast();
  const busy = state.checking || state.sending;
  const address = state.email ? ` ${state.email}` : "";

  const handleResend = async () => {
    if (busy) return;
    try {
      const outcome = await state.resend();
      if (outcome === "already-verified") {
        showToast("E-mail už je ověřený – můžete se přihlásit na závod.", "success");
      } else if (outcome === "sent") {
        showToast(
          `Ověřovací e-mail jsme poslali${address ? ` na${address}` : ""}. Klikněte na odkaz v něm (zkontrolujte i spam).`,
          "success"
        );
      }
    } catch (err) {
      reportWarning("EmailVerificationNotice.resend", err);
      showToast(
        firebaseErrorMessage(err, "Ověřovací e-mail se nepodařilo odeslat. Zkuste to prosím znovu."),
        "error"
      );
    }
  };

  const handleRecheck = async () => {
    if (busy) return;
    try {
      const ok = await state.recheck();
      showToast(
        ok
          ? "E-mail je ověřený – můžete se přihlásit na závod."
          : "E-mail zatím není ověřený. Klikněte na odkaz v ověřovacím e-mailu a zkuste to znovu.",
        ok ? "success" : "warning"
      );
    } catch (err) {
      reportWarning("EmailVerificationNotice.recheck", err);
      showToast(firebaseErrorMessage(err, "Ověření se nepodařilo zkontrolovat. Zkuste to prosím znovu."), "error");
    }
  };

  return (
    <InfoCard
      icon="bi-envelope-exclamation-fill"
      tone="warning"
      title={variant === "race" ? "Před přihlášením na závod ověřte e-mail" : "Ověřte svůj e-mail"}
      subtitle={
        <>
          {variant === "race"
            ? `Přihlásit se na závod jde jen s ověřeným e-mailem. Váš e-mail${address} zatím ověřený není.`
            : `Bez ověřeného e-mailu se nepřihlásíte na závod. Váš e-mail${address} zatím ověřený není.`}{" "}
          Klikněte na odkaz v ověřovacím e-mailu, pak se vraťte a stiskněte „Už mám ověřeno“.
        </>
      }
      className={className}
    >
      <div className="lks-infocard__actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleResend}
          disabled={busy || state.cooldown > 0}
        >
          {state.sending ? (
            <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
          ) : (
            <i className="bi bi-envelope me-2" aria-hidden="true" />
          )}
          {state.cooldown > 0
            ? `Poslat znovu (${state.cooldown} s)`
            : "Poslat ověřovací e-mail znovu"}
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={handleRecheck}
          disabled={busy}
        >
          {state.checking ? (
            <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
          ) : (
            <i className="bi bi-arrow-clockwise me-2" aria-hidden="true" />
          )}
          Už mám ověřeno
        </button>
      </div>
    </InfoCard>
  );
};

export default EmailVerificationNotice;
